import {$session} from "dot-framework"
import Swal from "sweetalert2"
import moment from "moment"
import UAParser from "ua-parser-js"
import {useCookies} from "vue3-cookies"
import {i18n} from "@/locale/i18n"
import {resolveInitLocaleCode} from "@/util/initLocale.mjs"

const {cookies} = useCookies()
const Locales = require("@/asset/data/locales.json")
const brailleLanguageList = require("@/asset/data/brailleLanguageList.json")

// 화면 언어 코드와 점자 목록 코드가 다른 언어. 스웨덴어는 se, 덴마크어는 dk, 세르비아어(키릴, sh)는 sr 로 적혀 있다.
// 맞는 점자가 없는 화면 언어(nl·ro·hu·cy)는 아래 기본 점자(영어)로 간다.
const BRAILLE_CODE_BY_LOCALE = {sv: "se", da: "dk", sh: "sr"}
// 목록 맨 앞(아랍어)을 기본으로 두면 맞는 점자가 없는 언어 사용자가 아랍어 점자로 시작했다.
const DEFAULT_BRAILLE = brailleLanguageList.find(e => e.code === "en" && e.default) ?? brailleLanguageList[0]

export default {
    getExtension(fileName) {
        if (!fileName)
            return ''

        const fileSeparator = fileName.lastIndexOf(".")
        if (fileSeparator === -1)
            return ''
        

        return fileName.substring(fileSeparator + 1).toLowerCase()
    },
    gotoAccount() {
        const callback = `${window.location.protocol}//${window.location.host}/login-callback`
        const accountInfoUrl = $session.getAccountInfoUrl(callback)

        window.location.href = accountInfoUrl
    },
    async swalActive(options) {
        return await Swal.fire(options)
    },
    isExist(val) {
        return val !== undefined && val !== null
    },
    isString(val) {
        if (!val) return false

        return typeof val === 'string' && this.removeSpace(val) !== ''
    },
    isFunction(val) {
        return val !== undefined && val !== null && typeof val === 'function'
    },
    isEmptyObject(obj) {
        return obj === null || obj === undefined || typeof obj !== 'object' || Object.keys(obj).length === 0
    },
    removeSpace(val) {
        if (!val) return val

        return val.replaceAll(/\s/g, '')
    },
    textToBraille(target, targetText) {
        if (!targetText) {
            return ""
        }

        let hexData = targetText
        if (target === "content") {
            if (Array.isArray(hexData)) {
                hexData = targetText.join(' \n ')
            }
        } else {
            if (target === "") {
                hexData = hexData.replace(/\s/g, "")

                if (hexData.length % 40 !== 0) {
                    const ZERO_HEX_CHAR = "0"
                    const paddingCount = 40 - (hexData.length % 40)

                    hexData += ZERO_HEX_CHAR.repeat(paddingCount)
                }
            }
        }

        return hexData
    },
    getRandomNumber(size = 1) {
        const random = Math.random()
        const pow = Math.pow(10, size)

        return Math.floor(random * pow)
    },
    secondToTimeFormat(second, format = 'mm:ss') {
        if (!second) return format.replaceAll(/[a-zA-Z]/g, "0")

        // 길이(초)라서 시간대를 타면 안 된다. moment(ms) 는 지역 시각으로 읽어 인도(+5:30) 같은 곳에서 30분이 더해졌다.
        // 'mm:ss' 는 60분을 넘기면 0분으로 돌아가므로 한 시간 이상은 시를 앞에 붙인다.
        const shownFormat = format === 'mm:ss' && second >= 3600 ? 'H:mm:ss' : format
        return moment.utc(second * 1000).format(shownFormat)
    },
    getInitLocale() {
        // 쿠키(설정 창에서 고른 언어) → 브라우저 언어 순서. 목록에 없는 언어(벵골어·덴마크어 등)면 영어로 시작한다.
        // 없던 값을 돌려주면 간편모드 첫 설정이 멈춘다
        const code = resolveInitLocaleCode({
            cookieLocale: cookies.get("user_locale"),
            navigatorLanguage: navigator.language,
            availableLocales: i18n.global.availableLocales,
            listedCodes: Locales.map(e => e.code),
        })
        return this.getLocale(code) ?? this.getLocale("en")
    },
    getLocale(code) {
        return Locales.find(e => e.code === code)
    },
    getBrailleByLocale(localeCode) {
        const brailleCode = BRAILLE_CODE_BY_LOCALE[localeCode] ?? localeCode
        const braille =  brailleLanguageList.find(e => e.code === brailleCode && e.default)
        return braille ?? DEFAULT_BRAILLE
    },
    getBraille(data) {
        const braille = brailleLanguageList.find(e => {
            return e.grade === data.grade
                && e.kind === data.kind
                && e.language === data.language
                && e.pin === data.pin
                && e.code === data.code
        })

        return braille ?? DEFAULT_BRAILLE
    },
    isMacOs() {
        const parser = new UAParser()
        const os = parser.getOS()
        
        return os.name === "Mac OS"
    },
    downloadDtmFile(item) {
        if (!item) return

        const fileExtension = item.fileExtension
        const dtmNo = item.fileKey
        if (["dtm", "dtms"].includes(fileExtension)) {
            const hasAudio = item.dtmsJson && typeof item.dtmsJson === 'object' && item.dtmsJson.audioPath !== ""
            if (hasAudio) {
                window.open(`/drive-app/v1/dtma/images?DTM_NO=${dtmNo}`)
            }
            else {
                const fileName = item.dtmImg
                window.open(`/drive-app/file/${fileName}`)
            }
        }
        else {
            window.open(`/drive-app/v1/doc/download?dtmNo=${dtmNo}`)
        }
    },
    isCtrlKey (event) {
        return event.ctrlKey || (this.isMacOs() && event.metaKey)
    },
    isAltKey (event) {
        return event.altKey
    },
    removeTrailingNumberInParentheses(str) {
        return str.replace(/\s*\(\d+\)$/, '')
    },
    decodeHtmlEntities(text) {
        const textArea = document.createElement('textarea')
        textArea.innerHTML = text
        return textArea.value
    },
    encodeHtmlEntities(text) {
        const textArea = document.createElement('textarea')
        textArea.textContent = text
        return textArea.innerHTML
    }
}